// BotContainer.tsx - COMPLETE REWRITE: Proper hierarchy for edit mode
import React, { useState, useEffect } from 'react';
import Header from "../../components/header";
import BotForm from './BotForm';
import BotList from './BotList';
import BotMetrics from './BotMetrics';
import UltimatePlanInfo from '../../components/UltimatePlanInfo/UltimatePlanInfo';
import { Container } from 'reactstrap';
import logger from '../../utils/logger';

type ViewMode = 'list' | 'create' | 'edit' | 'metrics';

const BotContainer: React.FC = () => {
  logger.log('🏠 BotContainer: Component rendered - COMPLETE REWRITE VERSION');

  // 🔥 CORE STATE: View management
  const [currentView, setCurrentView] = useState<ViewMode>('list');
  const [editingBotId, setEditingBotId] = useState<string | null>(null);
  const [currentTab, setCurrentTab] = useState<'form' | 'metrics'>('form');
  
  // 🔥 NEW STATE: Form state tracking for container-level actions
  const [isFormValid, setIsFormValid] = useState(true);
  const [isFormSaving, setIsFormSaving] = useState(false);

  // 🔥 NEW STATE: Ultimate plan check (same as Profile component)
  const [isUltimate, setIsUltimate] = useState<boolean | null>(null);

  logger.log('📊 BotContainer: Current state:', { 
    currentView, 
    editingBotId, 
    currentTab,
    isFormValid,
    isFormSaving,
    isUltimate
  });

  // 🔥 NEW: Ultimate plan check (identical to Profile component logic)
  useEffect(() => {
    try {
      logger.log('🔍 BotContainer: Checking ultimate plan status');
      const localData = window.localStorage?.getItem("userData");
      if (localData) {
        const userData = JSON.parse(localData);
        const planType = userData?.subscriptionPlanType;
        const isUltimatePlan = typeof planType === 'string' && planType.toLowerCase() === "ultimate";
        
        logger.log('📋 BotContainer: Plan check result:', { 
          planType, 
          isUltimatePlan,
          userDataExists: !!userData 
        });
        
        setIsUltimate(isUltimatePlan);
      } else {
        logger.warn('❌ BotContainer: No userData found in localStorage');
        setIsUltimate(false);
      }
    } catch (error) {
      console.error('❌ BotContainer: Error checking ultimate plan:', error);
      setIsUltimate(false);
    }
  }, []);

  // 🔥 NAVIGATION HANDLERS: Core view switching logic
  
  // Handle navigation to create new bot
  const handleCreateNew = () => {
    logger.log('➕ BotContainer: Navigating to create new bot');
    setCurrentView('create');
    setEditingBotId(null);
    setCurrentTab('form');
    // Reset form state
    setIsFormValid(true);
    setIsFormSaving(false);
  };

  // Handle navigation to edit existing bot
  const handleEditBot = (botId: string) => {
    logger.log('✏️ BotContainer: Navigating to edit bot:', botId);
    setCurrentView('edit');
    setEditingBotId(botId);
    setCurrentTab('form'); // Always start with form tab
    // Reset form state
    setIsFormValid(true);
    setIsFormSaving(false);
  };

  // Handle successful form submission (create or edit)
  const handleFormSuccess = () => {
    logger.log('✅ BotContainer: Form submitted successfully, returning to list');
    setCurrentView('list');
    setEditingBotId(null);
    setCurrentTab('form');
    // Reset form state
    setIsFormValid(true);
    setIsFormSaving(false);
  };

  // Handle form cancellation
  const handleFormCancel = () => {
    logger.log('❌ BotContainer: Form cancelled, returning to list');
    setCurrentView('list');
    setEditingBotId(null);
    setCurrentTab('form');
    // Reset form state
    setIsFormValid(true);
    setIsFormSaving(false);
  };

  // Handle tab switching in edit mode
  const handleTabSwitch = (tab: 'form' | 'metrics') => {
    logger.log('🔄 BotContainer: Switching to tab:', tab);
    setCurrentTab(tab);
  };

  // 🔥 NEW HANDLERS: Form state management
  
  // Handle form state updates from BotForm component
  const handleFormStateChange = (isValid: boolean, isSaving: boolean) => {
    logger.log('📊 BotContainer: Form state changed:', { isValid, isSaving });
    setIsFormValid(isValid);
    setIsFormSaving(isSaving);
  };

  // Handle form submission from container level (triggered by Update button)
  const handleContainerFormSubmit = () => {
    logger.log('🚀 BotContainer: Triggering form submission from container');
    
    // Find the form element and trigger submit event
    const form = document.getElementById('bot-form') as HTMLFormElement;
    if (form) {
      // Create and dispatch a submit event
      const submitEvent = new Event('submit', { 
        cancelable: true, 
        bubbles: true 
      });
      form.dispatchEvent(submitEvent);
    } else {
      console.error('❌ BotContainer: Form element not found');
    }
  };

  // 🔥 RENDER LOGIC: Main view rendering with complete edit structure

  const renderCurrentView = () => {
    logger.log('🔄 BotContainer: Rendering view:', currentView);

    switch (currentView) {
      
      // 📝 CREATE MODE: Standard form with its own header and actions
      case 'create':
        logger.log('📝 BotContainer: Rendering create form');
        return (
          <BotForm
            isEdit={false}
            onSuccess={handleFormSuccess}
            onCancel={handleFormCancel}
            hideHeader={false} // Keep original header for create mode
          />
        );

      // ✏️ EDIT MODE: Container-managed header and actions
      case 'edit':
        logger.log('✏️ BotContainer: Rendering edit mode with proper structure for bot:', editingBotId);
        
        // Safety check: Ensure we have a botId
        if (!editingBotId) {
          console.error('❌ BotContainer: Edit mode but no botId provided');
          setCurrentView('list');
          return null;
        }
        
        return (
          <div id="main" className="main">
            <Header />
            <Container fluid className="py-4">
              
              {/* 🔥 INTEGRATED: Header with Title, Actions AND Tabs - Single Container */}
              <div style={{ 
                background: 'white', 
                borderRadius: '12px', 
                marginBottom: '24px', 
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                overflow: 'hidden'
              }}>
                
                {/* Top Section: Title and Actions */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  padding: '32px 40px 24px 40px',
                  borderBottom: '1px solid #f3f4f6'
                }}>
                  
                  {/* Left: Title and Description */}
                  <div>
                    <h1 style={{
                      fontFamily: 'Gilroy, sans-serif',
                      fontSize: '36px',
                      fontWeight: '700',
                      color: '#111827',
                      margin: '0 0 8px 0',
                      letterSpacing: '-0.025em'
                    }}>
                      Edit Bot
                    </h1>
                    <p style={{
                      fontFamily: 'Gilroy, sans-serif',
                      fontSize: '16px',
                      fontWeight: '500',
                      color: '#6b7280',
                      margin: '0'
                    }}>
                      Set up bots that automatically send WhatsApp template messages when a client messages you with specific keywords
                    </p>
                  </div>
                  
                  {/* Right: Action Buttons */}
                  <div style={{ 
                    display: 'flex', 
                    gap: '16px', 
                    alignItems: 'center' 
                  }}>
                    
                    {/* Cancel Button */}
                    <button 
                      type="button" 
                      onClick={handleFormCancel}
                      disabled={isFormSaving}
                      style={{
                        background: 'white',
                        color: isFormSaving ? '#9ca3af' : '#6b7280',
                        border: '2px solid #d1d5db',
                        padding: '14px 28px',
                        borderRadius: '8px',
                        fontFamily: 'Gilroy, sans-serif',
                        fontWeight: '700',
                        cursor: isFormSaving ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        fontSize: '14px',
                        opacity: isFormSaving ? 0.6 : 1
                      }}
                      onMouseEnter={(e) => {
                        if (!isFormSaving) {
                          e.currentTarget.style.background = '#f9fafb';
                          e.currentTarget.style.borderColor = '#9ca3af';
                          e.currentTarget.style.color = '#374151';
                          e.currentTarget.style.transform = 'translateY(-1px)';
                          e.currentTarget.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isFormSaving) {
                          e.currentTarget.style.background = 'white';
                          e.currentTarget.style.borderColor = '#d1d5db';
                          e.currentTarget.style.color = '#6b7280';
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }
                      }}
                    >
                      Cancel
                    </button>
                    
                    {/* Update Bot Button */}
                    <button 
                      type="button"
                      onClick={handleContainerFormSubmit}
                      disabled={!isFormValid || isFormSaving}
                      style={{
                        background: (!isFormValid || isFormSaving) 
                          ? '#9ca3af' 
                          : 'linear-gradient(135deg, #3FAEFD 0%, #2563eb 100%)',
                        color: 'white',
                        border: 'none',
                        padding: '14px 36px',
                        borderRadius: '8px',
                        fontFamily: 'Gilroy, sans-serif',
                        fontWeight: '700',
                        cursor: (!isFormValid || isFormSaving) ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        minWidth: '160px',
                        fontSize: '14px',
                        boxShadow: (!isFormValid || isFormSaving) ? 'none' : '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                        opacity: (!isFormValid || isFormSaving) ? 0.6 : 1
                      }}
                      onMouseEnter={(e) => {
                        if (isFormValid && !isFormSaving) {
                          e.currentTarget.style.background = 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)';
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (isFormValid && !isFormSaving) {
                          e.currentTarget.style.background = 'linear-gradient(135deg, #3FAEFD 0%, #2563eb 100%)';
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                        }
                      }}
                    >
                      {isFormSaving ? (
                        <>
                          <span style={{ marginRight: '8px' }}>⏳</span>
                          Updating...
                        </>
                      ) : (
                        'Update Bot'
                      )}
                    </button>
                  </div>
                </div>

                {/* Bottom Section: Tab Navigation - INTEGRATED */}
                <div style={{ 
                  display: 'flex', 
                  padding: '0 32px',
                  background: '#f9fafb'
                }}>
                  
                  {/* Edit Bot Tab */}
                  <button
                    onClick={() => handleTabSwitch('form')}
                    style={{
                      padding: '16px 24px',
                      border: 'none',
                      background: 'transparent',
                      fontFamily: 'Gilroy, sans-serif',
                      fontWeight: '700',
                      fontSize: '16px',
                      cursor: 'pointer',
                      borderBottom: currentTab === 'form' ? '3px solid #3FAEFD' : '3px solid transparent',
                      color: currentTab === 'form' ? '#3FAEFD' : '#6b7280',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (currentTab !== 'form') {
                        e.currentTarget.style.color = '#374151';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentTab !== 'form') {
                        e.currentTarget.style.color = '#6b7280';
                      }
                    }}
                  >
                    ✏️ Edit Bot
                  </button>
                  
                  {/* Metrics Tab */}
                  <button
                    onClick={() => handleTabSwitch('metrics')}
                    style={{
                      padding: '16px 24px',
                      border: 'none',
                      background: 'transparent',
                      fontFamily: 'Gilroy, sans-serif',
                      fontWeight: '700',
                      fontSize: '16px',
                      cursor: 'pointer',
                      borderBottom: currentTab === 'metrics' ? '3px solid #3FAEFD' : '3px solid transparent',
                      color: currentTab === 'metrics' ? '#3FAEFD' : '#6b7280',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (currentTab !== 'metrics') {
                        e.currentTarget.style.color = '#374151';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentTab !== 'metrics') {
                        e.currentTarget.style.color = '#6b7280';
                      }
                    }}
                  >
                    📊 Metrics
                  </button>
                </div>
              </div>

              {/* 🔥 TAB CONTENT: Form or Metrics */}
              {currentTab === 'form' ? (
                <BotForm
                  isEdit={true}
                  botId={editingBotId}
                  onSuccess={handleFormSuccess}
                  onCancel={handleFormCancel}
                  hideHeader={true} // Hide form header - container manages it
                  onFormStateChange={handleFormStateChange} // Pass state handler
                />
              ) : (
                <BotMetrics botId={editingBotId} />
              )}
            </Container>
          </div>
        );

      // 📋 LIST MODE: Standard bot list
      case 'list':
      default:
        logger.log('📋 BotContainer: Rendering bot list');
        return (
          <BotList
            onCreateNew={handleCreateNew}
            onEditBot={handleEditBot}
          />
        );
    }
  };

  // 🔥 MAIN RENDER: No wrapper - each component handles its layout
  
  // 🔥 NEW: Early return for non-ultimate users (same as Profile component)
  if (isUltimate === false) {
    logger.log('🚫 BotContainer: Non-ultimate user detected, showing upgrade prompt');
    return (
      <div id="main" className="main">
        <Header />
        <UltimatePlanInfo />
      </div>
    );
  }

  // 🔥 LOADING: Show loading state while checking plan status
  if (isUltimate === null) {
    logger.log('⏳ BotContainer: Checking plan status...');
    return (
      <div id="main" className="main">
        <Header />
        <Container fluid className="py-4">
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            color: '#6b7280',
            fontFamily: 'Gilroy, sans-serif'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid #e5e7eb',
              borderTop: '3px solid #3FAEFD',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginBottom: '16px'
            }}></div>
            <p>Checking access permissions...</p>
          </div>
        </Container>
      </div>
    );
  }

  // 🔥 MAIN CONTENT: Only for ultimate users
  return <>{renderCurrentView()}</>;
};

export default BotContainer;